// src/pages/Dashboard.js

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Navigation, Route, Briefcase, User, ChevronRight, ChevronUp,
  Clock, MapPin, Tag, CheckCircle, RefreshCw,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { reports as reportsApi, trips as tripsApi, dealer as dealerApi } from '../services/api';

// ─── Brand colours ────────────────────────────────────────────────────────────
const PRIMARY      = '#00ACC1';
const PRIMARY_DARK = '#00838f';
const BIZ_COLOR    = '#FF9800';
const BIZ_LIGHT    = '#FFB74D';
const PRIV_COLOR   = '#4CAF50';
const PRIV_LIGHT   = '#81C784';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtKm = (km) => parseFloat(km || 0).toFixed(1);

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const monthName = (str) => {
  if (!str) return '';
  const [, m] = str.split('-');
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m) - 1] || m;
};

// Current SA tax year range
const taxYearRange = () => {
  const now  = new Date();
  const year = now.getMonth() >= 2 ? now.getFullYear() : now.getFullYear() - 1;
  return { date_from: `${year}-03-01`, date_to: `${year + 1}-02-28` };
};

// ─── Particle grid (identical to GeoDrive) ────────────────────────────────────
const ParticleGrid = () => {
  const canvasRef = useRef(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId, particles = [], mouse = { x: -1000, y: -1000 };
    const dotColor  = isDark ? '174,174,178' : '0,172,193';
    const baseAlpha = isDark ? 0.06 : 0.12;

    const resize = () => {
      canvas.width  = canvas.offsetWidth  * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      particles = [];
      const sp = 55;
      const cols = Math.ceil(canvas.offsetWidth  / sp);
      const rows = Math.ceil(canvas.offsetHeight / sp);
      for (let i = 0; i < cols; i++)
        for (let j = 0; j < rows; j++)
          particles.push({ x: i*sp+sp/2, y: j*sp+sp/2, baseX: i*sp+sp/2, baseY: j*sp+sp/2, size: 1.2, baseAlpha });
    };

    const onMove  = (e) => { const r = canvas.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; };
    const onLeave = ()  => { mouse.x = -1000; mouse.y = -1000; };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      particles.forEach(p => {
        const dx = mouse.x - p.baseX, dy = mouse.y - p.baseY;
        const dist = Math.sqrt(dx*dx + dy*dy);
        if (dist < 100) {
          const f = (100 - dist) / 100, a = Math.atan2(dy, dx);
          p.x = p.baseX - Math.cos(a)*f*15;
          p.y = p.baseY - Math.sin(a)*f*15;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size + f*1.5, 0, Math.PI*2);
          ctx.fillStyle = `rgba(${dotColor},${p.baseAlpha + f*0.35})`; ctx.fill();
        } else {
          p.x += (p.baseX - p.x) * 0.1;
          p.y += (p.baseY - p.y) * 0.1;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI*2);
          ctx.fillStyle = `rgba(${dotColor},${p.baseAlpha})`; ctx.fill();
        }
      });
      particles.forEach(p1 => {
        if (Math.sqrt((mouse.x-p1.x)**2 + (mouse.y-p1.y)**2) < 100)
          particles.forEach(p2 => {
            if (p1 === p2) return;
            const d = Math.sqrt((p1.x-p2.x)**2 + (p1.y-p2.y)**2);
            if (d < 65) {
              ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y);
              ctx.strokeStyle = `rgba(${dotColor},0.05)`; ctx.lineWidth = 0.5; ctx.stroke();
            }
          });
      });
      animId = requestAnimationFrame(animate);
    };

    resize();
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', resize);
    animate();

    return () => {
      cancelAnimationFrame(animId);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', resize);
    };
  }, [isDark]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }} />;
};

// ─── Back to top ──────────────────────────────────────────────────────────────
const BackToTop = () => {
  const [v, setV] = useState(false);
  useEffect(() => {
    const t = () => setV(window.scrollY > 400);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg hover:scale-110 transition-all duration-500 ${v ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}
      style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})`, boxShadow: '0 4px 14px rgba(0,172,193,0.4)' }}
    >
      <ChevronUp size={24} />
    </button>
  );
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const ChartTooltip = ({ x, y, visible, children }) => {
  if (!visible) return null;
  return (
    <div className="absolute z-50 pointer-events-none"
      style={{ left: x, top: y - 10, transform: 'translate(-50%, -100%)' }}>
      <div className="px-3 py-2 rounded-xl text-xs font-medium shadow-lg"
        style={{ backgroundColor: '#1C1C1E', color: '#f5f5f5', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
        {children}
      </div>
      <div className="w-2 h-2 rotate-45 mx-auto -mt-1" style={{ backgroundColor: '#1C1C1E' }} />
    </div>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
const Card = ({ children, className = '', theme }) => (
  <div
    className={`rounded-2xl border transition-all ${className}`}
    style={{
      backgroundColor: theme.card,
      borderColor:     theme.border,
      boxShadow:       theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)',
    }}
  >
    {children}
  </div>
);

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Icon, color, theme }) => (
  <Card theme={theme} className="p-5 hover:scale-[1.01] cursor-default">
    <div className="flex flex-col items-center text-center">
      <div className="p-2.5 rounded-xl mb-3"
        style={{ backgroundColor: `${color}18` }}>
        <Icon size={18} style={{ color }} strokeWidth={1.8} />
      </div>
      <p className="text-2xl font-bold tracking-tight" style={{ color: theme.text }}>{value}</p>
      <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>{label}</p>
      {sub && <p className="text-xs mt-1 font-medium" style={{ color: theme.textSecondary }}>{sub}</p>}
    </div>
  </Card>
);

// ─── Monthly bar chart ────────────────────────────────────────────────────────
const MonthlyBarChart = ({ data, theme }) => {
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, content: null });

  if (!data || !data.length) return (
    <p className="text-xs py-8 text-center" style={{ color: theme.textMuted }}>No monthly data yet</p>
  );

  const max  = Math.max(...data.map(d => parseFloat(d.business_km||0) + parseFloat(d.private_km||0)), 1);
  const barW = Math.min(48, Math.max(20, 700 / data.length - 10));

  return (
    <div className="relative overflow-x-auto">
      <ChartTooltip x={tip.x} y={tip.y} visible={tip.visible}>{tip.content}</ChartTooltip>
      <div className="flex items-end gap-2 justify-center" style={{ height: 225, minWidth: data.length * (barW + 10) }}>
        {data.map((d, i) => {
          const bKm = parseFloat(d.business_km || 0);
          const pKm = parseFloat(d.private_km  || 0);
          return (
            <div key={i} className="flex flex-col items-center" style={{ width: barW }}>
              <div className="w-full flex flex-col items-stretch justify-end cursor-pointer" style={{ height: 200 }}
                onMouseEnter={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const par  = e.currentTarget.closest('.relative').getBoundingClientRect();
                  setTip({
                    visible: true,
                    x: rect.left - par.left + rect.width / 2,
                    y: rect.top  - par.top,
                    content: (
                      <div className="space-y-1">
                        <p className="font-semibold">{monthName(d.month)}</p>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BIZ_COLOR }} />
                          <span>Business: {bKm.toFixed(1)} km</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PRIV_COLOR }} />
                          <span>Private: {pKm.toFixed(1)} km</span>
                        </div>
                        <p style={{ opacity: 0.6 }}>Total: {(bKm + pKm).toFixed(1)} km</p>
                      </div>
                    ),
                  });
                }}
                onMouseLeave={() => setTip(p => ({ ...p, visible: false }))}
              >
                {bKm > 0 && (
                  <div className="w-full rounded-t-lg transition-all duration-500"
                    style={{ height: `${(bKm / max) * 100}%`, background: `linear-gradient(180deg, ${BIZ_LIGHT}, ${BIZ_COLOR})`, minHeight: 3 }} />
                )}
                {pKm > 0 && (
                  <div className="w-full rounded-b-lg transition-all duration-500"
                    style={{ height: `${(pKm / max) * 100}%`, background: `linear-gradient(180deg, ${PRIV_LIGHT}, ${PRIV_COLOR})`, minHeight: 3 }} />
                )}
              </div>
              <span className="mt-1.5" style={{ fontSize: 10, color: theme.textMuted }}>{monthName(d.month)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Donut chart ──────────────────────────────────────────────────────────────
const DonutChart = ({ value1, value2, label1, label2, theme }) => {
  const total = value1 + value2;
  if (!total) return <p className="text-xs text-center py-4" style={{ color: theme.textMuted }}>No data</p>;

  const pct1  = value1 / total;
  const r = 52, cx = 65, cy = 65, sw = 16, circ = 2 * Math.PI * r;

  return (
    <div className="flex flex-col items-center">
      <svg width={130} height={130} viewBox="0 0 130 130">
        <defs>
          <linearGradient id="td1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={BIZ_LIGHT} />
            <stop offset="100%" stopColor={BIZ_COLOR} />
          </linearGradient>
          <linearGradient id="td2" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={PRIV_LIGHT} />
            <stop offset="100%" stopColor={PRIV_COLOR} />
          </linearGradient>
        </defs>
        {/* Track */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={theme.border} strokeWidth={sw} />
        {/* Business arc */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#td1)" strokeWidth={sw}
          strokeDasharray={`${circ * pct1} ${circ * (1 - pct1)}`}
          strokeDashoffset={circ * 0.25} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
        {/* Private arc */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#td2)" strokeWidth={sw}
          strokeDasharray={`${circ * (1 - pct1)} ${circ * pct1}`}
          strokeDashoffset={circ * 0.25 - circ * pct1} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
        <text x={cx} y={cy - 5}  textAnchor="middle" fontSize="22" fontWeight="700" fill={theme.text}>{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill={theme.textMuted}>trips</text>
      </svg>
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BIZ_COLOR }} />
          <span className="text-xs" style={{ color: theme.textSecondary }}>{label1} ({value1})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PRIV_COLOR }} />
          <span className="text-xs" style={{ color: theme.textSecondary }}>{label2} ({value2})</span>
        </div>
      </div>
    </div>
  );
};

// ─── Recent trip row ──────────────────────────────────────────────────────────
const RecentTrip = ({ trip, onClick, theme }) => {
  const isBiz      = trip.category === 'business';
  const accentColor = isBiz ? BIZ_COLOR : PRIV_COLOR;
  const endLabel    = trip.end_zone_name || trip.end_address || null;

  return (
    <div onClick={onClick}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all"
      style={{ ':hover': { backgroundColor: theme.background } }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.background}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${accentColor}18` }}>
        <Navigation size={13} style={{ color: accentColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: theme.text }}>
            {fmtKm(trip.distance_km)} km
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
            style={{
              backgroundColor: `${accentColor}18`,
              color: accentColor,
            }}>
            {isBiz ? 'Business' : 'Private'}
          </span>
        </div>
        <p className="text-xs truncate mt-0.5" style={{ color: theme.textMuted }}>
          {new Date(trip.start_time).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}{' '}
          {new Date(trip.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {endLabel ? ` — ${endLabel}` : ''}
        </p>
      </div>
      <ChevronRight size={14} style={{ color: theme.textMuted }} />
    </div>
  );
};

// ─── User Dashboard ────────────────────────────────────────────────────────────
function UserDashboard({ user, theme }) {
  const navigate = useNavigate();
  const [summary,      setSummary]      = useState(null);
  const [recentTrips,  setRecentTrips]  = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    const { date_from, date_to } = taxYearRange();
    Promise.all([
      reportsApi.summary({ date_from, date_to }),
      tripsApi.list({ page: 1, status: 'completed' }),
    ]).then(([s, t]) => {
      if (s.success && s.data) setSummary(s.data);
      if (t.success && t.data) setRecentTrips(t.data.data?.slice(0, 5) || []);
    }).finally(() => setLoading(false));
  }, []);

  const userName = user?.display_name || user?.email?.split('@')[0] || 'there';

  if (loading) return (
    <div className="flex items-center justify-center min-h-[80vh]">
      <div className="w-8 h-8 border-2 rounded-full animate-spin"
        style={{ borderColor: theme.border, borderTopColor: PRIMARY }} />
    </div>
  );

  const totals = summary?.totals;

  return (
    <div className="relative px-4 md:px-8 py-6 md:py-8 min-h-screen">
      <ParticleGrid />
      <div className="relative z-[1]">

        {/* Greeting */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.text }}>
            {getGreeting()},{' '}
            <span style={{ color: PRIMARY }}>{userName}</span>
          </h1>
          <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
            Here's your driving overview for this tax year.
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          <StatCard label="Total Trips"    value={totals?.total_trips     || 0}       icon={Navigation} color={PRIMARY}     theme={theme} />
          <StatCard label="Total Distance" value={`${fmtKm(totals?.total_km)} km`}    icon={Route}      color={PRIMARY}     theme={theme} />
          <StatCard label="Business"       value={`${fmtKm(totals?.business_km)} km`} icon={Briefcase}  color={BIZ_COLOR}   theme={theme}
            sub={`${totals?.business_trips || 0} trips`} />
          <StatCard label="Private"        value={`${fmtKm(totals?.private_km)} km`}  icon={User}       color={PRIV_COLOR}  theme={theme}
            sub={`${totals?.private_trips || 0} trips`} />
        </div>

        {/* Monthly chart + donut */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-6">
          <Card theme={theme} className="p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold" style={{ color: theme.text }}>Monthly Distance</h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: `linear-gradient(135deg, ${BIZ_LIGHT}, ${BIZ_COLOR})` }} />
                  <span className="text-xs" style={{ color: theme.textMuted }}>Business</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ background: `linear-gradient(135deg, ${PRIV_LIGHT}, ${PRIV_COLOR})` }} />
                  <span className="text-xs" style={{ color: theme.textMuted }}>Private</span>
                </div>
              </div>
            </div>
            <MonthlyBarChart data={summary?.by_month} theme={theme} />
          </Card>
          <Card theme={theme} className="p-5 flex flex-col">
            <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>Trip Split</h3>
            <div className="flex-1 flex items-center justify-center">
              <DonutChart
                value1={parseInt(totals?.business_trips || 0)}
                value2={parseInt(totals?.private_trips  || 0)}
                label1="Business" label2="Private"
                theme={theme}
              />
            </div>
          </Card>
        </div>

        {/* Distance breakdown + recent trips */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-6">
          {/* Distance breakdown */}
          <Card theme={theme} className="p-5">
            <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>Distance Breakdown</h3>
            <div className="text-center mb-4">
              <p className="text-xs" style={{ color: theme.textMuted }}>Total Driven</p>
              <p className="text-3xl font-bold mt-1" style={{ color: theme.text }}>
                {fmtKm(totals?.total_km)} km
              </p>
            </div>
            <div className="rounded-xl p-3 space-y-2.5" style={{ backgroundColor: theme.background }}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BIZ_COLOR }} />
                  <span className="text-xs" style={{ color: theme.textSecondary }}>Business</span>
                </div>
                <span className="text-sm font-semibold" style={{ color: BIZ_COLOR }}>
                  {fmtKm(totals?.business_km)} km
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PRIV_COLOR }} />
                  <span className="text-xs" style={{ color: theme.textSecondary }}>Private</span>
                </div>
                <span className="text-sm font-semibold" style={{ color: PRIV_COLOR }}>
                  {fmtKm(totals?.private_km)} km
                </span>
              </div>
              {totals?.unclassified_trips > 0 && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.textMuted }} />
                    <span className="text-xs" style={{ color: theme.textSecondary }}>Unclassified</span>
                  </div>
                  <span className="text-xs font-semibold" style={{ color: theme.textMuted }}>
                    {totals.unclassified_trips} trips
                  </span>
                </div>
              )}
              <div className="pt-2" style={{ borderTop: `1px solid ${theme.border}` }}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium" style={{ color: theme.text }}>Total</span>
                  <span className="text-sm font-bold" style={{ color: theme.text }}>
                    {fmtKm(totals?.total_km)} km
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Recent trips */}
          <Card theme={theme} className="p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: theme.text }}>Recent Trips</h3>
              <Link to="/portal/trips" className="text-xs font-medium" style={{ color: PRIMARY }}>
                View all
              </Link>
            </div>
            {recentTrips.length > 0 ? (
              <div className="space-y-0.5">
                {recentTrips.map(t => (
                  <RecentTrip
                    key={t.id}
                    trip={t}
                    theme={theme}
                    onClick={() => navigate(`/portal/trips/${t.id}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock size={24} className="mx-auto mb-2 opacity-40" style={{ color: theme.textMuted }} />
                <p className="text-xs" style={{ color: theme.textMuted }}>No trips recorded yet</p>
                <p className="text-xs mt-1" style={{ color: theme.textMuted }}>
                  Trips tracked in the app will appear here
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* Top reasons */}
        {summary?.by_reason?.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 mb-6">
            <Card theme={theme} className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold" style={{ color: theme.text }}>Top Reasons</h3>
                <Link to="/portal/reports" className="text-xs font-medium" style={{ color: PRIMARY }}>
                  Full report
                </Link>
              </div>
              <div className="space-y-3">
                {summary.by_reason.slice(0, 6).map((r, i) => {
                  const isBiz  = r.category === 'business';
                  const color  = isBiz ? BIZ_COLOR : PRIV_COLOR;
                  const maxKm  = parseFloat(summary.by_reason[0].total_km || 1);
                  const pct    = Math.round((parseFloat(r.total_km) / maxKm) * 100);
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium truncate flex-1 mr-2" style={{ color: theme.text }}>
                          {r.reason || 'No reason'}
                        </span>
                        <span className="text-xs shrink-0" style={{ color: theme.textMuted }}>
                          {parseFloat(r.total_km).toFixed(1)} km
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: theme.background }}>
                        <div className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Plan status card */}
            <Card theme={theme} className="p-5">
              <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>Account</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-3 px-4 rounded-xl"
                  style={{ backgroundColor: theme.background }}>
                  <div>
                    <p className="text-xs font-semibold" style={{ color: theme.textMuted }}>Plan</p>
                    <p className="text-sm font-bold mt-0.5" style={{ color: theme.text }}>
                      {user?.tier === 'pro' ? 'Pro' : 'Free'}
                    </p>
                  </div>
                  {user?.tier !== 'pro' && (
                    <Link to="/portal/upgrade"
                      className="text-xs font-bold px-3 py-1.5 rounded-xl text-white"
                      style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})` }}>
                      Upgrade
                    </Link>
                  )}
                </div>
                <Link to="/portal/reports"
                  className="flex items-center justify-between py-3 px-4 rounded-xl transition-opacity hover:opacity-70"
                  style={{ backgroundColor: theme.background }}>
                  <p className="text-sm font-medium" style={{ color: theme.text }}>Export logbook</p>
                  <ChevronRight size={14} style={{ color: theme.textMuted }} />
                </Link>
                <Link to="/portal/account"
                  className="flex items-center justify-between py-3 px-4 rounded-xl transition-opacity hover:opacity-70"
                  style={{ backgroundColor: theme.background }}>
                  <p className="text-sm font-medium" style={{ color: theme.text }}>Account settings</p>
                  <ChevronRight size={14} style={{ color: theme.textMuted }} />
                </Link>
              </div>
            </Card>
          </div>
        )}
      </div>
      <BackToTop />
    </div>
  );
}

// ─── Dealer Dashboard (unchanged layout, same style) ──────────────────────────
function DealerDashboard({ user, theme }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dealerApi.dashboard().then(res => {
      if (res.success) setData(res.data);
      setLoading(false);
    });
  }, []);

  const stats = data?.stats;
  const batch = data?.batch;
  const userName = user?.display_name || user?.email?.split('@')[0] || 'there';

  const statCards = [
    { icon: Tag,         label: 'Total Codes', value: stats?.total_codes     || 0, color: theme.text },
    { icon: Clock,       label: 'Available',   value: stats?.available_codes || 0, color: PRIMARY },
    { icon: RefreshCw,   label: 'Issued',      value: stats?.issued_codes    || 0, color: '#f59e0b' },
    { icon: CheckCircle, label: 'Redeemed',    value: stats?.used_codes      || 0, color: PRIV_COLOR },
  ];

  return (
    <div className="relative px-4 md:px-8 py-6 md:py-8 min-h-screen">
      <ParticleGrid />
      <div className="relative z-[1]">

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.text }}>
            {getGreeting()},{' '}
            <span style={{ color: PRIMARY }}>{userName}</span>
          </h1>
          <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
            {batch?.name || 'Dealer portal'}
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ backgroundColor: theme.card }} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {statCards.map(s => (
              <StatCard key={s.label} label={s.label} value={s.value} icon={s.icon} color={s.color} theme={theme} />
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/portal/dealers"
            className="flex items-center gap-4 p-5 rounded-2xl transition-all hover:opacity-80"
            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})` }}>
              <Tag size={19} color="white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: theme.text }}>Issue a Voucher</p>
              <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>Get the next available code</p>
            </div>
            <ChevronRight size={16} style={{ color: theme.textMuted }} />
          </Link>
          <Link to="/portal/account"
            className="flex items-center gap-4 p-5 rounded-2xl transition-all hover:opacity-80"
            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})` }}>
              <User size={19} color="white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: theme.text }}>Account</p>
              <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>Update your profile and password</p>
            </div>
            <ChevronRight size={16} style={{ color: theme.textMuted }} />
          </Link>
        </div>
      </div>
      <BackToTop />
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const theme    = useTheme();
  const isDealer = !!user?.voucher_batch_id;

  if (isDealer) return <DealerDashboard user={user} theme={theme} />;
  return <UserDashboard user={user} theme={theme} />;
}